import React, { useState, useEffect, useRef } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Chip,
  Grid,
  FormControlLabel,
  Checkbox,
  Stack,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  IconButton,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  TrendingUp as MetricIcon,
  Lightbulb as InspirationIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import useRoadmapStore from '../store/roadmapStore';
import PageContainer from '../components/PageContainer';
import StepPageHeader from '../components/StepPageHeader';
import { STEP_LABELS } from '../utils/constants';
import useAISuggest from '../hooks/useAISuggest';

export default function VisionPage() {
  const { 
    getStepData, 
    updateStepData, 
    markStepComplete, 
    isStepCompleted,
    canEdit
  } = useRoadmapStore();
  const { t } = useTranslation();
  const handleAISuggest = useAISuggest('vision');
  const _storeStep = useRoadmapStore(s => s.roadmapData?.steps?.vision);

  const readOnly = !canEdit();
  const stepData = getStepData('vision');
  const [visionStatement, setVisionStatement] = useState(stepData.vision_statement || '');
  const [missionStatement, setMissionStatement] = useState(stepData.mission_statement || '');
  const [purpose, setPurpose] = useState(stepData.purpose || '');
  const [values, setValues] = useState(stepData.values || []);
  const [timeframe, setTimeframe] = useState(stepData.timeframe || '3-5');
  const [stakeholders, setStakeholders] = useState(stepData.stakeholders || []);
  const [metrics, setMetrics] = useState(stepData.success_metrics || []);
  const [inspirationSources, setInspirationSources] = useState(stepData.inspiration_sources || []);
  
  const [newValue, setNewValue] = useState('');
  const [newStakeholder, setNewStakeholder] = useState({ name: '', role: '' });
  const [newMetric, setNewMetric] = useState({ description: '', target: '', measurable: true });
  const [newInspiration, setNewInspiration] = useState('');
  const [isComplete, setIsComplete] = useState(isStepCompleted('vision'));

  useEffect(() => {
    const data = getStepData('vision');
    setVisionStatement(data.vision_statement || '');
    setMissionStatement(data.mission_statement || '');
    setPurpose(data.purpose || '');
    setValues(data.values || []);
    setTimeframe(data.timeframe || '3-5');
    setStakeholders(data.stakeholders || []);
    setMetrics(data.success_metrics || []);
    setInspirationSources(data.inspiration_sources || []);
    setIsComplete(isStepCompleted('vision'));
  }, [getStepData, isStepCompleted, _storeStep]);

  const handleSave = () => {
    updateStepData('vision', {
      vision_statement: visionStatement,
      mission_statement: missionStatement,
      purpose: purpose,
      values: values,
      timeframe: timeframe,
      stakeholders: stakeholders,
      success_metrics: metrics,
      inspiration_sources: inspirationSources
    });
  };

  // Keep a ref to the latest handleSave so the unmount cleanup always uses current state
  const _saveRef = useRef(null);
  useEffect(() => { _saveRef.current = handleSave; });
  useEffect(() => () => { if (_saveRef.current) _saveRef.current(); }, []); // eslint-disable-line

  const handleAddValue = () => {
    if (newValue.trim()) {
      const updatedValues = [...values, newValue.trim()];
      setValues(updatedValues);
      setNewValue('');
      updateStepData('vision', {
        vision_statement: visionStatement,
        mission_statement: missionStatement,
        purpose: purpose,
        values: updatedValues,
        timeframe: timeframe,
        stakeholders: stakeholders,
        success_metrics: metrics,
        inspiration_sources: inspirationSources
      });
    }
  };

  const handleDeleteValue = (index) => {
    const updatedValues = values.filter((_, i) => i !== index);
    setValues(updatedValues);
    updateStepData('vision', {
      vision_statement: visionStatement,
      mission_statement: missionStatement,
      purpose: purpose,
      values: updatedValues,
      timeframe: timeframe,
      stakeholders: stakeholders,
      success_metrics: metrics,
      inspiration_sources: inspirationSources
    });
  };

  const handleAddStakeholder = () => {
    if (newStakeholder.name.trim() && newStakeholder.role.trim()) {
      const updatedStakeholders = [...stakeholders, { ...newStakeholder }];
      setStakeholders(updatedStakeholders);
      setNewStakeholder({ name: '', role: '' });
      updateStepData('vision', {
        vision_statement: visionStatement,
        mission_statement: missionStatement,
        purpose: purpose,
        values: values,
        timeframe: timeframe,
        stakeholders: updatedStakeholders,
        success_metrics: metrics,
        inspiration_sources: inspirationSources
      });
    }
  };

  const handleDeleteStakeholder = (index) => {
    const updatedStakeholders = stakeholders.filter((_, i) => i !== index);
    setStakeholders(updatedStakeholders);
    updateStepData('vision', {
      vision_statement: visionStatement,
      mission_statement: missionStatement,
      purpose: purpose,
      values: values,
      timeframe: timeframe,
      stakeholders: updatedStakeholders,
      success_metrics: metrics,
      inspiration_sources: inspirationSources
    });
  };

  const handleAddMetric = () => {
    if (newMetric.description.trim() && newMetric.target.trim()) {
      const updatedMetrics = [...metrics, { ...newMetric }];
      setMetrics(updatedMetrics);
      setNewMetric({ description: '', target: '', measurable: true });
      updateStepData('vision', {
        vision_statement: visionStatement,
        mission_statement: missionStatement,
        purpose: purpose,
        values: values,
        timeframe: timeframe,
        stakeholders: stakeholders,
        success_metrics: updatedMetrics,
        inspiration_sources: inspirationSources
      });
    }
  };

  const handleDeleteMetric = (index) => {
    const updatedMetrics = metrics.filter((_, i) => i !== index);
    setMetrics(updatedMetrics);
    updateStepData('vision', {
      vision_statement: visionStatement,
      mission_statement: missionStatement,
      purpose: purpose,
      values: values,
      timeframe: timeframe,
      stakeholders: stakeholders,
      success_metrics: updatedMetrics,
      inspiration_sources: inspirationSources
    });
  };

  const handleAddInspiration = () => {
    if (newInspiration.trim()) {
      const updatedInspirations = [...inspirationSources, newInspiration.trim()];
      setInspirationSources(updatedInspirations);
      setNewInspiration('');
      updateStepData('vision', {
        vision_statement: visionStatement,
        mission_statement: missionStatement,
        purpose: purpose,
        values: values,
        timeframe: timeframe,
        stakeholders: stakeholders,
        success_metrics: metrics,
        inspiration_sources: updatedInspirations
      });
    }
  };

  const handleDeleteInspiration = (index) => {
    const updatedInspirations = inspirationSources.filter((_, i) => i !== index);
    setInspirationSources(updatedInspirations);
    updateStepData('vision', {
      vision_statement: visionStatement,
      mission_statement: missionStatement,
      purpose: purpose,
      values: values,
      timeframe: timeframe,
      stakeholders: stakeholders,
      success_metrics: metrics,
      inspiration_sources: updatedInspirations
    });
  };

  const handleCompleteToggle = () => {
    const newCompleteState = !isComplete;
    const success = markStepComplete('vision', newCompleteState);
    setIsComplete(success);
  };

  const canComplete = visionStatement.trim() && missionStatement.trim() && purpose.trim() && values.length > 0;

  return (
    <PageContainer maxWidth={900}>
      <StepPageHeader
        stepNum={1}
        title={t(STEP_LABELS.vision)}
        intro={t('stepContent.vision.intro')}
        isComplete={isComplete}
        canComplete={canComplete}
        onToggleComplete={handleCompleteToggle}
        readOnly={readOnly}
        requireMessage={t('stepContent.vision.alerts.requireFields')}
      onAISuggest={handleAISuggest}
        />
      <Paper elevation={2} sx={{ p: { xs: 3, md: 4 } }}>
        <Grid container spacing={3}>
          {/* Vision Statement */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              {t('stepContent.vision.sections.vision.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('stepContent.vision.sections.vision.description')}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              label={t('stepContent.vision.sections.vision.label')}
              placeholder={t('stepContent.vision.sections.vision.placeholder')}
              value={visionStatement}
              onChange={(e) => setVisionStatement(e.target.value)}
              onBlur={handleSave}
              variant="outlined"
              disabled={readOnly}
            />
          </Grid>

          {/* Mission Statement */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Mission Statement
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Define your organization's purpose and how it will achieve the vision
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Mission Statement"
              placeholder="We will [action] for [target audience] by [method]..."
              value={missionStatement}
              onChange={(e) => setMissionStatement(e.target.value)}
              onBlur={handleSave}
              variant="outlined"
              disabled={readOnly}
            />
          </Grid>

          {/* Vision Timeframe */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Vision Timeframe
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              How far into the future does this vision extend?
            </Typography>
            <FormControl fullWidth disabled={readOnly}>
              <InputLabel>Timeframe</InputLabel>
              <Select
                value={timeframe}
                onChange={(e) => { setTimeframe(e.target.value); handleSave(); }}
                label="Timeframe"
                disabled={readOnly}
              >
                <MenuItem value="1-2">1-2 years (Short-term)</MenuItem>
                <MenuItem value="3-5">3-5 years (Medium-term)</MenuItem>
                <MenuItem value="5-10">5-10 years (Long-term)</MenuItem>
                <MenuItem value="10+">10+ years (Visionary)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Purpose */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              {t('stepContent.vision.sections.purpose.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('stepContent.vision.sections.purpose.description')}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              label={t('stepContent.vision.sections.purpose.label')}
              placeholder={t('stepContent.vision.sections.purpose.placeholder')}
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              onBlur={handleSave}
              variant="outlined"
              disabled={readOnly}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>

          {/* Core Values */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              {t('stepContent.vision.sections.values.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('stepContent.vision.sections.values.description')}
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, mb: 2 }}>
              {!readOnly && (
                <>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('stepContent.vision.fields.newValueLabel')}
                    placeholder={t('stepContent.vision.fields.newValuePlaceholder')}
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleAddValue();
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddValue}
                    disabled={!newValue.trim()}
                    startIcon={<AddIcon />}
                  >
                    {t('stepContent.vision.actions.addValue')}
                  </Button>
                </>
              )}
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {values.map((value, index) => (
                <Chip
                  key={index}
                  label={value}
                  onDelete={readOnly ? undefined : () => handleDeleteValue(index)}
                  deleteIcon={<DeleteIcon />}
                  color="primary"
                  variant="outlined"
                />
              ))}
              {values.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {t('stepContent.vision.empty.values')}
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>

          {/* Key Stakeholders */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon color="primary" />
              Key Stakeholders
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Identify primary stakeholders who will be impacted by or contribute to this vision
            </Typography>
            
            {!readOnly && (
              <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Stakeholder Name"
                    placeholder="e.g., Board of Directors, Customers, Employees..."
                    value={newStakeholder.name}
                    onChange={(e) => setNewStakeholder({ ...newStakeholder, name: e.target.value })}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Role/Interest"
                    placeholder="e.g., Decision makers, Primary beneficiaries..."
                    value={newStakeholder.role}
                    onChange={(e) => setNewStakeholder({ ...newStakeholder, role: e.target.value })}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddStakeholder}
                    disabled={!newStakeholder.name.trim() || !newStakeholder.role.trim()}
                    startIcon={<AddIcon />}
                    fullWidth
                  >
                    Add Stakeholder
                  </Button>
                </Stack>
              </Card>
            )}

            <Stack spacing={2}>
              {stakeholders.map((stakeholder, index) => (
                <Card key={index} variant="outlined">
                  <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {stakeholder.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {stakeholder.role}
                      </Typography>
                    </Box>
                    {!readOnly && (
                      <IconButton onClick={() => handleDeleteStakeholder(index)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </CardContent>
                </Card>
              ))}
              {stakeholders.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                  No stakeholders added yet
                </Typography>
              )}
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>

          {/* SMART Success Metrics */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MetricIcon color="primary" />
              Success Metrics (SMART)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Define Specific, Measurable, Achievable, Relevant, and Time-bound metrics to track vision achievement
            </Typography>
            
            {!readOnly && (
              <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Metric Description"
                    placeholder="e.g., Achieve $10M annual revenue"
                    value={newMetric.description}
                    onChange={(e) => setNewMetric({ ...newMetric, description: e.target.value })}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Target/Goal"
                    placeholder="e.g., By end of Year 3"
                    value={newMetric.target}
                    onChange={(e) => setNewMetric({ ...newMetric, target: e.target.value })}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={newMetric.measurable}
                        onChange={(e) => setNewMetric({ ...newMetric, measurable: e.target.checked })}
                      />
                    }
                    label="This metric is measurable with data"
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddMetric}
                    disabled={!newMetric.description.trim() || !newMetric.target.trim()}
                    startIcon={<AddIcon />}
                    fullWidth
                  >
                    Add Metric
                  </Button>
                </Stack>
              </Card>
            )}

            <Stack spacing={2}>
              {metrics.map((metric, index) => (
                <Card key={index} variant="outlined">
                  <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {metric.description}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Target: {metric.target}
                      </Typography>
                      {metric.measurable && (
                        <Chip label="Measurable" size="small" color="success" sx={{ mt: 1 }} />
                      )}
                    </Box>
                    {!readOnly && (
                      <IconButton onClick={() => handleDeleteMetric(index)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </CardContent>
                </Card>
              ))}
              {metrics.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                  No success metrics defined yet
                </Typography>
              )}
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>

          {/* Inspiration Sources */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InspirationIcon color="primary" />
              Inspiration Sources
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              What companies, leaders, or models inspire this vision?
            </Typography>
            
            {!readOnly && (
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Inspiration Source"
                  placeholder="e.g., Tesla's sustainability mission, Apple's design philosophy..."
                  value={newInspiration}
                  onChange={(e) => setNewInspiration(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddInspiration();
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleAddInspiration}
                  disabled={!newInspiration.trim()}
                  startIcon={<AddIcon />}
                >
                  Add
                </Button>
              </Box>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {inspirationSources.map((source, index) => (
                <Chip
                  key={index}
                  label={source}
                  onDelete={readOnly ? undefined : () => handleDeleteInspiration(index)}
                  deleteIcon={<DeleteIcon />}
                  color="secondary"
                  variant="outlined"
                />
              ))}
              {inspirationSources.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No inspiration sources added yet
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Save Button */}
          <Grid item xs={12}>
            <Button
              variant="contained"
              size="large"
              onClick={handleSave}
              disabled={readOnly}
              sx={{ mt: 2 }}
            >
              {t('stepContent.vision.actions.save', { step: t(STEP_LABELS.vision) })}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </PageContainer>
  );
}
